import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { Activity } from '../../types/Activity';
import {
  ActivityCity,
  ActivityPrice,
  ActivitySummary,
  fetchTourCities,
  fetchTourPrices,
  fetchTours,
} from '../../api/Tours';
import '../../css/HotelListModal.css';

interface ActivityListModalProps {
  dayIndex: number;
  place: { label: string; value: string };
  travelDate?: string;
  onClose: () => void;
  onSelect: (activity: Activity) => void;
}

type CityOption = {
  value: string;
  label: string;
  cityId: number;
};

const PRICE_WARNING_MESSAGE = 'Live prices unavailable. Showing list prices only.';

const toCityOption = (city: ActivityCity): CityOption => ({
  value: String(city.cityId),
  label: city.cityName || `City ${city.cityId}`,
  cityId: city.cityId,
});

const normalizeText = (value: string) => value.trim().toLowerCase();

const pickDefaultCity = (cities: CityOption[], place: { label: string; value: string }): CityOption | null => {
  if (!cities.length) return null;

  const placeValueAsNumber = Number(place.value);
  if (Number.isFinite(placeValueAsNumber)) {
    const exact = cities.find((city) => city.cityId === placeValueAsNumber);
    if (exact) return exact;
  }

  const candidates = [place.label, place.value]
    .map((item) => normalizeText(item || ''))
    .filter(Boolean);
  if (candidates.length > 0) {
    const matched = cities.find((city) => candidates.includes(normalizeText(city.label)));
    if (matched) return matched;
  }

  return cities[0];
};

const mapTourToActivity = (tour: ActivitySummary, city: CityOption): Activity => ({
  id: tour.id,
  tour_id: tour.id,
  slug: `tour-${tour.id}`,
  name: tour.name,
  description: tour.description || undefined,
  picture_url: tour.image_path || undefined,
  category: tour.city_tour_type || undefined,
  city: city.label,
  city_id: city.cityId,
  currency: 'INR',
  is_active: true,
});

const mergeActivitiesWithPrices = (items: Activity[], prices: Record<string, ActivityPrice>): Activity[] =>
  items.map((item) => {
    const key = String(item.tour_id ?? item.id ?? '');
    const price = prices[key];
    if (!price) return item;

    const next: Activity = { ...item };
    if (typeof price.amount === 'number' && Number.isFinite(price.amount)) {
      next.from_price_paise = Math.round(price.amount * 100);
    }
    if (price.contractId !== undefined) {
      next.contract_id = price.contractId;
    }
    if (price.currency) {
      next.currency = price.currency;
    }
    return next;
  });

const ActivityListModal: React.FC<ActivityListModalProps> = ({
  dayIndex,
  place,
  travelDate,
  onClose,
  onSelect,
}) => {
  const [cities, setCities] = useState<CityOption[]>([]);
  const [selectedCity, setSelectedCity] = useState<CityOption | null>(null);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loadingCities, setLoadingCities] = useState(true);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [priceWarning, setPriceWarning] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'price' | 'duration'>('name');
  const [priceJobId, setPriceJobId] = useState<string | null>(null);
  const menuPortalTarget = typeof document !== 'undefined' ? document.body : null;
  const placeLabel = place.label;
  const placeValue = place.value;

  useEffect(() => {
    let cancelled = false;

    const loadCities = async () => {
      setLoadingCities(true);
      setError(null);
      try {
        const response = await fetchTourCities();
        if (cancelled) return;
        const options = response.map(toCityOption);
        setCities(options);
        setSelectedCity(pickDefaultCity(options, { label: placeLabel, value: placeValue }));
      } catch {
        if (cancelled) return;
        setError('Failed to load cities. Please try again.');
        setCities([]);
        setSelectedCity(null);
      } finally {
        if (!cancelled) {
          setLoadingCities(false);
        }
      }
    };

    void loadCities();

    return () => {
      cancelled = true;
    };
  }, [placeLabel, placeValue]);

  const loadActivities = useCallback(
    async (searchOverride?: string) => {
      if (!selectedCity) {
        setActivities([]);
        setPriceJobId(null);
        return;
      }

      const activeNameFilter = (searchOverride ?? appliedSearchTerm).trim();
      setLoadingActivities(true);
      setError(null);
      setPriceWarning(null);

      try {
        const response = await fetchTours({
          page: 1,
          limit: 50,
          cityId: selectedCity.cityId,
          name: activeNameFilter || undefined,
          travelDate: travelDate || undefined,
        });

        setActivities(response.items.map((tour) => mapTourToActivity(tour, selectedCity)));
        setPriceJobId(response.priceJobId || null);
      } catch {
        setError('Failed to load activities. Please try again.');
        setActivities([]);
        setPriceJobId(null);
      } finally {
        setLoadingActivities(false);
      }
    },
    [appliedSearchTerm, selectedCity, travelDate]
  );

  useEffect(() => {
    void loadActivities();
  }, [loadActivities]);

  useEffect(() => {
    if (!priceJobId) return;

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let attempts = 0;

    const pollPrices = async () => {
      attempts += 1;
      try {
        const response = await fetchTourPrices(priceJobId);
        if (cancelled) return;

        if (response.status === 'SUCCESS') {
          setActivities((prev) => mergeActivitiesWithPrices(prev, response.prices));
          return;
        }

        if (response.status === 'FAILED') {
          setPriceWarning(PRICE_WARNING_MESSAGE);
          return;
        }
      } catch {
        if (!cancelled) {
          setPriceWarning(PRICE_WARNING_MESSAGE);
        }
        return;
      }

      if (attempts >= 30) {
        setPriceWarning(PRICE_WARNING_MESSAGE);
        return;
      }

      timeoutId = setTimeout(pollPrices, 1500);
    };

    void pollPrices();

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [priceJobId]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const sortedActivities = useMemo(() => {
    const toPrice = (activity: Activity) =>
      typeof activity.from_price_paise === 'number' ? activity.from_price_paise : Number.MAX_SAFE_INTEGER;
    const toDuration = (activity: Activity) =>
      typeof activity.duration_minutes === 'number' ? activity.duration_minutes : Number.MAX_SAFE_INTEGER;

    return [...activities].sort((a, b) => {
      if (sortBy === 'price') return toPrice(a) - toPrice(b);
      if (sortBy === 'duration') return toDuration(a) - toDuration(b);
      return a.name.localeCompare(b.name);
    });
  }, [activities, sortBy]);

  const isInitialLoading = loadingCities || (loadingActivities && activities.length === 0);

  if (isInitialLoading) {
    return (
      <div className="modal-backdrop">
        <div className="hotel-modal">
          <div className="loading">Loading activities...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="hotel-modal" onClick={(event) => event.stopPropagation()}>
        <button className="close-btn" onClick={onClose} aria-label="Close modal" title="Close modal">
          ×
        </button>

        <div className="hotel-search-form">
          <div className="hs-field">
            <label>City</label>
            <Select<CityOption, false>
              options={cities}
              isSearchable
              onChange={(option) => setSelectedCity(option)}
              value={selectedCity}
              placeholder="Select city"
              noOptionsMessage={() => 'No cities found'}
              menuPortalTarget={menuPortalTarget}
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: 40,
                }),
                menu: (base) => ({
                  ...base,
                  zIndex: 10001,
                }),
                menuPortal: (base) => ({
                  ...base,
                  zIndex: 10001,
                }),
              }}
            />
          </div>
          <div className="hs-field">
            <label>Search Activities</label>
            <input
              type="text"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Search by name"
            />
          </div>
        </div>

        <div className="hotel-controls">
          <div className="hc-sort">
            <span>Sort by:</span>
            <button className={sortBy === 'name' ? 'active' : ''} onClick={() => setSortBy('name')}>
              Name
            </button>
            <button className={sortBy === 'price' ? 'active' : ''} onClick={() => setSortBy('price')}>
              Price
            </button>
            <button className={sortBy === 'duration' ? 'active' : ''} onClick={() => setSortBy('duration')}>
              Duration
            </button>
          </div>
          <div className="hc-filter">
            <span>Found {sortedActivities.length} activities</span>
          </div>
          <div className="hc-filter">
            <button
              className="hs-search-btn"
              onClick={() => {
                const nextSearch = searchInput.trim();
                setAppliedSearchTerm(nextSearch);
                if (nextSearch === appliedSearchTerm) {
                  void loadActivities(nextSearch);
                }
              }}
            >
              Search
            </button>
          </div>
        </div>

        {priceWarning && <p className="package-editor-small-note">{priceWarning}</p>}

        <div className="hotel-list">
          {error && <p className="error">{error}</p>}
          {!loadingActivities && !error && sortedActivities.length === 0 && (
            <p className="no-results">No activities found. Try adjusting your search criteria.</p>
          )}
          {!error && sortedActivities.map((activity, index) => (
            <div className="hotel-card" key={activity.id || index}>
              <div className="hotel-main">
                <img
                  src={activity.picture_url || '/placeholder-activity.png'}
                  alt={activity.name}
                  className="hotel-thumbnail"
                  onError={(event) => {
                    event.currentTarget.style.display = 'none';
                  }}
                />
                <div className="hotel-info">
                  <h3 className="hotel-name">{activity.name}</h3>
                  <div className="hotel-stars">
                    {activity.category && (
                      <span className="category-badge">{activity.category}</span>
                    )}
                  </div>
                  <div className="hotel-amenities">
                    {activity.duration_minutes && (
                      <span className="amenity-badge">
                        {Math.floor(activity.duration_minutes / 60)}h {activity.duration_minutes % 60}m
                      </span>
                    )}
                    {activity.city && (
                      <span className="amenity-badge">{activity.city}</span>
                    )}
                    {activity.city_code && (
                      <span className="amenity-badge">{activity.city_code}</span>
                    )}
                  </div>
                  {activity.description && (
                    <div className="activity-description">
                      {activity.description.length > 100
                        ? `${activity.description.substring(0, 100)}...`
                        : activity.description}
                    </div>
                  )}
                </div>
                <div className="hotel-rate">
                  <span className="hotel-price">
                    {typeof activity.from_price_paise === 'number'
                      ? `₹${Math.round(activity.from_price_paise / 100).toLocaleString('en-IN')}`
                      : 'N/A'}
                  </span>
                </div>
              </div>
              <div className="hotel-action">
                <button className="select-btn" onClick={() => onSelect(activity)}>
                  Select
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ActivityListModal;
